// billing/CreateInvoiceModal.tsx
import React, { useState, useEffect } from 'react';
import {
    X, Plus, Minus, DollarSign, Building2, Mail, Phone,
    FileText, CheckCircle, ChevronLeft, ChevronRight, Receipt, User, FileSignature
} from 'lucide-react';
import { useBillingStore } from '../../store/billingStore';
import {
    CreateInvoiceDto,
    LineItem,
    TaxItem,
    BillTo,
    BillFrom,
} from '../../services/billing.service';
import { contractsService, Contract } from '../../services/contracts.service';

interface CreateInvoiceModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({ isOpen, onClose }) => {
    const { createInvoice, settings, fetchSettings } = useBillingStore();
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [contracts, setContracts] = useState<Contract[]>([]);
    const [selectedContractId, setSelectedContractId] = useState<string>('');

    const [formData, setFormData] = useState({
        projectId: '',
        contractId: '',
        type: 'milestone',
        billTo: {
            companyName: '',
            contactName: '',
            email: '',
            phone: '',
        } as BillTo,
        billFrom: {
            businessName: '',
            email: '',
            phone: '',
        } as BillFrom,
        lineItems: [{ description: '', quantity: 1, unitPrice: 0, amount: 0, taxable: true }] as LineItem[],
        taxes: [] as TaxItem[],
        paymentTerms: 'Net 30',
        paymentTermsDays: 30,
        notes: '',
    });

    const steps = [
        { number: 1, title: 'Client', icon: User },
        { number: 2, title: 'Items', icon: Receipt },
        { number: 3, title: 'Terms', icon: FileText },
    ];

    const invoiceTypes = [
        { value: 'milestone', label: 'Milestone' },
        { value: 'final', label: 'Final' },
        { value: 'quotation', label: 'Quotation' },
        { value: 'proforma', label: 'Proforma' },
        { value: 'adhoc', label: 'Ad-hoc' },
    ];

    const paymentTermsOptions = [
        { value: 'Due on Receipt', days: 0 },
        { value: 'Net 15', days: 15 },
        { value: 'Net 30', days: 30 },
        { value: 'Net 45', days: 45 },
        { value: 'Net 60', days: 60 },
    ];

    useEffect(() => {
        if (isOpen) {
            // Reset form to initial state when modal opens
            setFormData({
                projectId: '',
                contractId: '',
                type: 'milestone',
                billTo: { companyName: '', contactName: '', email: '', phone: '' },
                billFrom: { businessName: '', email: '', phone: '' },
                lineItems: [{ description: '', quantity: 1, unitPrice: 0, amount: 0, taxable: true }],
                taxes: [],
                paymentTerms: 'Net 30',
                paymentTermsDays: 30,
                notes: '',
            });
            setSelectedContractId('');
            setCurrentStep(1);
            setErrors({});

            // Fetch settings and contracts
            fetchSettings();
            contractsService.getAll({ status: 'active' }).then(setContracts).catch(console.error);
        }
    }, [isOpen, fetchSettings]);

    // Handle contract selection
    const handleContractSelect = (contractId: string) => {
        setSelectedContractId(contractId);
        const contract = contracts.find(c => c.id === contractId);

        if (contract) {
            setFormData(prev => ({
                ...prev,
                contractId: contractId,
                billTo: {
                    companyName: contract.partnership?.brand || '',
                    contactName: contract.partnership?.contact || '',
                    email: contract.partnership?.email || '',
                    phone: prev.billTo.phone || '',
                },
                lineItems: [{
                    description: `${contract.title} - Contract Services`,
                    quantity: 1,
                    unitPrice: contract.totalValue,
                    amount: contract.totalValue,
                    taxable: true,
                }],
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                contractId: '',
            }));
        }
    };

    useEffect(() => {
        if (settings?.businessInfo) {
            setFormData(prev => ({
                ...prev,
                billFrom: {
                    businessName: settings.businessInfo?.legalName || '',
                    email: settings.businessInfo?.email || '',
                    phone: settings.businessInfo?.phone || '',
                },
            }));
        }
        if (settings?.invoiceDefaults) {
            setFormData(prev => ({
                ...prev,
                paymentTerms: settings.invoiceDefaults?.paymentTerms || 'Net 30',
                paymentTermsDays: settings.invoiceDefaults?.paymentTermsDays || 30,
            }));
        }
    }, [settings]);

    const validateStep = (step: number): boolean => {
        const newErrors: Record<string, string> = {};

        if (step === 1) {
            // Validate billTo (client info)
            if (!formData.billTo.companyName.trim()) newErrors.companyName = 'Company name is required';
            if (!formData.billTo.email.trim()) newErrors.email = 'Email is required';
            if (formData.billTo.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billTo.email)) {
                newErrors.email = 'Please enter a valid email address';
            }

            // Validate billFrom (your business info)
            if (!formData.billFrom.businessName.trim()) {
                newErrors.businessName = 'Your business name is required. Please configure it in Billing Settings.';
            }
            if (!formData.billFrom.email.trim()) {
                newErrors.billFromEmail = 'Your business email is required. Please configure it in Billing Settings.';
            }
            if (formData.billFrom.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billFrom.email)) {
                newErrors.billFromEmail = 'Please enter a valid business email address';
            }
        }

        if (step === 2) {
            if (formData.lineItems.length === 0 || !formData.lineItems.some(item => item.description && item.amount > 0)) {
                newErrors.lineItems = 'At least one line item with description and amount is required';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => Math.min(prev + 1, 3));
        }
    };

    const handleBack = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
        setErrors({});
    };

    const updateLineItem = (index: number, field: keyof LineItem, value: any) => {
        const updated = [...formData.lineItems];
        (updated[index] as any)[field] = value;
        if (field === 'quantity' || field === 'unitPrice') {
            updated[index].amount = updated[index].quantity * updated[index].unitPrice;
        }
        setFormData({ ...formData, lineItems: updated });
    };

    const addLineItem = () => {
        setFormData({
            ...formData,
            lineItems: [...formData.lineItems, { description: '', quantity: 1, unitPrice: 0, amount: 0, taxable: true }],
        });
    };

    const removeLineItem = (index: number) => {
        if (formData.lineItems.length > 1) {
            setFormData({
                ...formData,
                lineItems: formData.lineItems.filter((_, i) => i !== index),
            });
        }
    };

    const calculateSubtotal = () => formData.lineItems.reduce((sum, item) => sum + item.amount, 0);
    const calculateTax = () => {
        const subtotal = calculateSubtotal();
        const taxRate = settings?.taxSettings?.defaultTaxRate || 0;
        return subtotal * (taxRate / 100);
    };
    const calculateTotal = () => calculateSubtotal() + calculateTax();

    const handleSubmit = async () => {
        if (!validateStep(1) || !validateStep(2)) {
            setCurrentStep(1);
            return;
        }

        setIsSubmitting(true);
        try {
            const taxAmount = calculateTax();

            // Build DTO excluding empty projectId/contractId to avoid MongoDB validation errors
            const dto: CreateInvoiceDto = {
                type: formData.type,
                billTo: formData.billTo,
                billFrom: formData.billFrom,
                lineItems: formData.lineItems,
                taxes: taxAmount > 0 ? [{
                    name: settings?.taxSettings?.taxName || 'Tax',
                    rate: settings?.taxSettings?.defaultTaxRate || 0,
                    amount: taxAmount,
                }] : [],
                paymentTerms: formData.paymentTerms,
                paymentTermsDays: formData.paymentTermsDays,
            };

            // Only include optional fields if they have values
            if (formData.projectId) {
                dto.projectId = formData.projectId;
            }
            if (formData.contractId) {
                dto.contractId = formData.contractId;
            }
            if (formData.notes) {
                dto.notes = formData.notes;
            }

            console.log('Invoice DTO being sent:', JSON.stringify(dto, null, 2));
            await createInvoice(dto);
            onClose();
            resetForm();
        } catch (error: any) {
            console.error('Failed to create invoice:', error);
            console.error('Error response:', error?.response?.data);
            console.error('Error message:', error?.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            projectId: '',
            contractId: '',
            type: 'milestone',
            billTo: { companyName: '', contactName: '', email: '', phone: '' },
            billFrom: { businessName: '', email: '', phone: '' },
            lineItems: [{ description: '', quantity: 1, unitPrice: 0, amount: 0, taxable: true }],
            taxes: [],
            paymentTerms: 'Net 30',
            paymentTermsDays: 30,
            notes: '',
        });
        setSelectedContractId('');
        setCurrentStep(1);
        setErrors({});
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
                {/* Backdrop */}
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

                {/* Modal */}
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Create Invoice
                        </h2>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Step Indicator */}
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            {steps.map((step, index) => (
                                <React.Fragment key={step.number}>
                                    <button
                                        onClick={() => setCurrentStep(step.number)}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${currentStep === step.number
                                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                                            : currentStep > step.number
                                                ? 'text-green-600 dark:text-green-400'
                                                : 'text-gray-400 dark:text-gray-500'
                                            }`}
                                    >
                                        <step.icon className="w-4 h-4" />
                                        <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
                                    </button>
                                    {index < steps.length - 1 && (
                                        <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.number
                                            ? 'bg-green-500'
                                            : 'bg-gray-200 dark:bg-gray-700'
                                            }`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto max-h-[50vh]">
                        {/* Step 1: Client Information */}
                        {currentStep === 1 && (
                            <div className="space-y-4">
                                {/* Contract Selector */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Link to Contract (Optional)
                                    </label>
                                    <div className="relative">
                                        <FileSignature className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <select
                                            value={selectedContractId}
                                            onChange={(e) => handleContractSelect(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 appearance-none cursor-pointer"
                                        >
                                            <option value="">-- Manual Entry --</option>
                                            {contracts.map(contract => (
                                                <option key={contract.id} value={contract.id}>
                                                    {contract.title} - {contract.partnership?.brand || 'Unknown'} (${contract.totalValue.toLocaleString()})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {selectedContractId && (
                                        <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                                            ✓ Client details auto-filled from contract
                                        </p>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Company Name <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="text"
                                                value={formData.billTo.companyName}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    billTo: { ...formData.billTo, companyName: e.target.value }
                                                })}
                                                className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ${errors.companyName ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-600'
                                                    }`}
                                                placeholder="e.g. TechFlow Solutions"
                                            />
                                        </div>
                                        {errors.companyName && <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.companyName}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Contact Name
                                        </label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="text"
                                                value={formData.billTo.contactName || ''}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    billTo: { ...formData.billTo, contactName: e.target.value }
                                                })}
                                                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                                placeholder="e.g. Sarah Johnson"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Email Address <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="email"
                                                value={formData.billTo.email}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    billTo: { ...formData.billTo, email: e.target.value }
                                                })}
                                                className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ${errors.email ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-600'
                                                    }`}
                                                placeholder="client@company.com"
                                            />
                                        </div>
                                        {errors.email && <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.email}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Phone Number
                                        </label>
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="tel"
                                                value={formData.billTo.phone || ''}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    billTo: { ...formData.billTo, phone: e.target.value }
                                                })}
                                                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                                placeholder="+1 (555) 123-4567"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Invoice Type Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Invoice Type
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {invoiceTypes.map(type => (
                                            <button
                                                key={type.value}
                                                type="button"
                                                onClick={() => setFormData({ ...formData, type: type.value })}
                                                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors border ${formData.type === type.value
                                                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 border-primary-500'
                                                    : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                                                    }`}
                                            >
                                                {type.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Business Info Validation Errors */}
                                {(errors.businessName || errors.billFromEmail) && (
                                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                                        <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                                            Business Information Required
                                        </p>
                                        {errors.businessName && (
                                            <p className="text-sm text-red-600 dark:text-red-400">{errors.businessName}</p>
                                        )}
                                        {errors.billFromEmail && (
                                            <p className="text-sm text-red-600 dark:text-red-400">{errors.billFromEmail}</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 2: Line Items */}
                        {currentStep === 2 && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Line Items
                                    </label>
                                    <button
                                        type="button"
                                        onClick={addLineItem}
                                        className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Item
                                    </button>
                                </div>

                                {errors.lineItems && <p className="text-red-600 dark:text-red-400 text-sm">{errors.lineItems}</p>}

                                <div className="space-y-3">
                                    {formData.lineItems.map((item, index) => (
                                        <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                                    Item {index + 1}
                                                </span>
                                                {formData.lineItems.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeLineItem(index)}
                                                        className="p-1 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                    >
                                                        <Minus className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Description</label>
                                                    <input
                                                        type="text"
                                                        value={item.description}
                                                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                                        placeholder="e.g., Video Production Services"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-3 gap-3">
                                                    <div>
                                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Qty</label>
                                                        <input
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                                                            min={1}
                                                            className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Unit Price</label>
                                                        <div className="relative">
                                                            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                                                            <input
                                                                type="number"
                                                                value={item.unitPrice}
                                                                onChange={(e) => updateLineItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                                                min={0}
                                                                step={0.01}
                                                                className="w-full pl-6 pr-2 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Amount</label>
                                                        <div className="px-3 py-2 bg-gray-100 dark:bg-gray-600 rounded-lg text-sm font-medium text-gray-900 dark:text-white">
                                                            ${item.amount.toFixed(2)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Totals */}
                                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl mt-4">
                                    <div className="flex justify-between text-gray-600 dark:text-gray-400 mb-2">
                                        <span>Subtotal</span>
                                        <span className="font-medium">${calculateSubtotal().toFixed(2)}</span>
                                    </div>
                                    {settings?.taxSettings?.defaultTaxRate && settings.taxSettings.defaultTaxRate > 0 && (
                                        <div className="flex justify-between text-gray-600 dark:text-gray-400 mb-2">
                                            <span>{settings.taxSettings.taxName} ({settings.taxSettings.defaultTaxRate}%)</span>
                                            <span className="font-medium">${calculateTax().toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-lg font-bold text-gray-900 dark:text-white pt-2 border-t border-gray-200 dark:border-gray-600">
                                        <span>Total</span>
                                        <span className="text-green-600 dark:text-green-400">${calculateTotal().toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Payment Terms & Notes */}
                        {currentStep === 3 && (
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Payment Terms
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {paymentTermsOptions.map(option => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => setFormData({
                                                    ...formData,
                                                    paymentTerms: option.value,
                                                    paymentTermsDays: option.days
                                                })}
                                                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors border ${formData.paymentTerms === option.value
                                                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 border-primary-500'
                                                    : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                                                    }`}
                                            >
                                                {option.value}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Notes for Client
                                    </label>
                                    <textarea
                                        value={formData.notes}
                                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                        rows={4}
                                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                                        placeholder="Add any additional notes, payment instructions, or terms..."
                                    />
                                </div>

                                {/* Invoice Summary */}
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                                    <h5 className="font-medium text-gray-900 dark:text-white mb-3">Invoice Summary</h5>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <span className="text-gray-600 dark:text-gray-400">Client:</span>
                                            <span className="ml-2 font-medium text-gray-900 dark:text-white">{formData.billTo.companyName}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600 dark:text-gray-400">Email:</span>
                                            <span className="ml-2 font-medium text-gray-900 dark:text-white">{formData.billTo.email}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600 dark:text-gray-400">Total:</span>
                                            <span className="ml-2 font-medium text-green-600 dark:text-green-400">${calculateTotal().toFixed(2)}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600 dark:text-gray-400">Terms:</span>
                                            <span className="ml-2 font-medium text-gray-900 dark:text-white">{formData.paymentTerms}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600 dark:text-gray-400">Type:</span>
                                            <span className="ml-2 font-medium capitalize text-gray-900 dark:text-white">{formData.type}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600 dark:text-gray-400">Items:</span>
                                            <span className="ml-2 font-medium text-gray-900 dark:text-white">{formData.lineItems.length}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        <button
                            type="button"
                            onClick={() => currentStep > 1 ? handleBack() : onClose()}
                            className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                            disabled={isSubmitting}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            {currentStep > 1 ? 'Back' : 'Cancel'}
                        </button>

                        {currentStep < 3 ? (
                            <button
                                type="button"
                                onClick={handleNext}
                                className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        Create Invoice
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateInvoiceModal;
